import iconArrow from '../../assets/icons/icon-arrow.svg';
import {Image, Link} from '@shopify/hydrogen';
import {useCallback, useState, useMemo, useEffect, useRef} from 'react';
import clsx from 'clsx';
import ReactSlider from 'react-slider';
import CurrencyInput from 'react-currency-input-field';
import {DATA_TEST_IDS, FILTER_SELECT_TYPES} from '~/lib/const';
import {IconCheckV2} from '~/components';
import {FilterStateType, FilterOption, FilterOptionValue} from '~/types';
import throttle from '~/lib/throttle';

const PRICE_RANGE_ARIA_LABEL = ['Lower thumb', 'Upper thumb'];
const PRICE_RANGE_ARIA_LABEL_VALUE_TEXT = (state: {valueNow: number}) =>
  `Thumb value ${state.valueNow}`;

const MAX_PRICE = 10000;

const useDefaultExpandedData = (data: {
  filters: FilterOption[];
  filterApplied: FilterStateType;
}) => {
  return useMemo(() => {
    const {filters, filterApplied} = data;

    const result = {} as {[key: string]: boolean};
    filters.forEach((option) => {
      if (filterApplied[option.filterOptionId] != null) {
        result[option.name] = true;
      }
    });
    return result;
  }, [data]);
};

function ProductFilterItemOption({
  filter,
  option,
  activeValue,
  onClickOption,
}: {
  filter: FilterOption;
  option: FilterOptionValue;
  activeValue: string | string[];
  onClickOption: (
    filter: FilterOption,
    option: {value: string} | (undefined | number)[],
  ) => void;
}) {
  const {label, value, color} = option ?? {};

  const isActive =
    activeValue != null &&
    (filter.selectType === FILTER_SELECT_TYPES.MULTIPLE
      ? activeValue.includes(String(value))
      : String(activeValue) === String(value));

  const WrapperComponent =
    filter.selectType === FILTER_SELECT_TYPES.LINK ? Link : 'button';

  const wrapperComponentProps =
    filter.selectType === FILTER_SELECT_TYPES.LINK
      ? {to: option.href}
      : {
          type: 'button',
          'aria-label': label,
          onClick: () => onClickOption(filter, option),
        };

  return (
    <li>
      {/* @ts-ignore */}
      <WrapperComponent
        className="flex items-start space-x-2.5"
        {...wrapperComponentProps}
      >
        <div
          className={clsx(
            'flex justify-center items-center w-[30px] h-[30px] border border-gray-100 rounded-[5px]',
            isActive ? 'bg-blue-500' : 'bg-white',
          )}
        >
          {isActive && (
            <IconCheckV2 data-testid={DATA_TEST_IDS.CHECKBOX('checked')} />
          )}
        </div>
        {filter.type === 'color' && (
          <div
            data-testid={DATA_TEST_IDS.FILTER_LABEL('color')}
            key={'color'}
            className="w-[26px] h-[26px] rounded-full"
            style={{
              backgroundColor: color ?? 'transparent',
            }}
          />
        )}
        <div
          data-testid={DATA_TEST_IDS.FILTER_LABEL(label?.toLowerCase())}
          className={'text-left'}
        >
          {label?.toUpperCase()}
        </div>
      </WrapperComponent>
    </li>
  );
}

function ProductFilterItem({
  filterState,
  isLast,
  filter,
  expanded,
  setExpanded,
  onClickOption,
}: {
  filterState: FilterStateType;
  isLast: boolean;
  filter: FilterOption;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<{[key: string]: boolean}>>;
  onClickOption: (
    filter: FilterOption,
    option: {value: string} | (undefined | number)[],
  ) => void;
}) {
  const handleClickExpand = useCallback(() => {
    setExpanded((prev) => ({
      ...prev,
      [filter.name]: !prev[filter.name] ?? true,
    }));
  }, [filter.name, setExpanded]);

  const handleBlurRange = useCallback(() => {
    const {min, max} =
      (filterState[filter.filterOptionId] as {min?: number; max?: number}) ??
      {};

    onClickOption(filter, [
      Math.max(Math.min(min ?? 0, filter.max ?? MAX_PRICE), filter.min ?? 0),
      Math.min(
        Math.max(max ?? MAX_PRICE, filter.min ?? 0),
        filter.max ?? MAX_PRICE,
      ),
    ]);
  }, [filter, filterState, onClickOption]);

  const handleValueChangeRangeMinInput = useCallback(
    (value?: string) => {
      onClickOption(filter, [Number(value ?? 0), undefined]);
    },
    [filter, onClickOption],
  );

  const handleValueChangeRangeMaxInput = useCallback(
    (value?: string) => {
      onClickOption(filter, [undefined, Number(value ?? 0)]);
    },
    [filter, onClickOption],
  );

  const handleChangeRange = useCallback(
    (values: number[]) => {
      onClickOption(filter, values);
    },
    [filter, onClickOption],
  );

  const optionLength = filter.options?.length ?? 1;
  const maxHeightClassName = clsx(
    optionLength < 5 && 'max-h-[300px]',
    optionLength <= 12 && 'max-h-[500px]',
    optionLength > 12 && optionLength < 24 && 'max-h-[1000px]',
    optionLength > 24 && 'max-h-[1500px]',
  );

  return (
    <div
      className={clsx(
        'border-t border-t-gray-300',
        isLast && 'border-b border-b-gray-300',
      )}
    >
      <button
        data-testid={DATA_TEST_IDS.BUTTON(filter.name)}
        type="button"
        className="flex w-full justify-between items-center py-5"
        aria-label={`Toggle ${filter.name} filter`}
        onClick={handleClickExpand}
      >
        <span className="font-header text-22/32 font-bold">{filter.name}</span>
        <Image
          loading="lazy"
          src={iconArrow}
          width={16.58}
          height={9.71}
          alt={filter.name}
          className={clsx('transition-all mr-2', {
            'rotate-180': expanded,
          })}
        />
      </button>

      <div
        className={clsx(
          'overflow-auto transition-max-height duration-500 ease-in-out',
          expanded ? maxHeightClassName : 'max-h-0',
        )}
      >
        {(filter.type === 'options' || filter.type === 'color') && (
          <ul className="font-header text-18/30 font-bold space-y-2.5 mb-[22px]">
            {filter.options?.map((option, index) => {
              const activeValue = filterState[filter.filterOptionId] as
                | string
                | string[];
              return (
                <ProductFilterItemOption
                  key={option.value ?? option.label ?? option.href ?? index}
                  activeValue={activeValue}
                  filter={filter}
                  option={option}
                  onClickOption={onClickOption}
                />
              );
            })}
          </ul>
        )}

        {filter.type === 'range' && (
          <div key="range" className="mb-[30px]">
            <ReactSlider
              data-testid={DATA_TEST_IDS.RANGE_FILTER}
              className="w-100 h-[22px] flex items-center"
              thumbClassName="w-[22px] h-[22px] bg-blue-500 rounded-full"
              trackClassName="slider-track"
              value={
                filterState[filter.filterOptionId] != null
                  ? [
                      // @ts-ignore
                      filterState[filter.filterOptionId].min,
                      // @ts-ignore
                      filterState[filter.filterOptionId].max,
                    ]
                  : [filter.min, filter.max]
              }
              min={filter.min ?? 0}
              max={filter.max ?? MAX_PRICE}
              ariaLabel={PRICE_RANGE_ARIA_LABEL}
              ariaValuetext={PRICE_RANGE_ARIA_LABEL_VALUE_TEXT}
              pearling
              minDistance={filter.hideCurrencySymbol ? 1 : 10}
              onAfterChange={handleChangeRange}
            />

            <div className="flex justify-between items-center mt-5">
              <CurrencyInput
                data-testid={DATA_TEST_IDS.INPUT('currency-min')}
                className="h-11 w-[77px] border border-gray-100 text-center font-medium text-18/26 p-2"
                value={
                  // @ts-ignore
                  filterState[filter.filterOptionId]?.min ?? filter.min
                }
                decimalsLimit={0}
                min={filter.min ?? 0}
                max={
                  // @ts-ignore
                  filterState[filter.filterOptionId]?.max ??
                  filter.max ??
                  MAX_PRICE
                }
                prefix={filter.hideCurrencySymbol ? '' : '$'}
                onValueChange={handleValueChangeRangeMinInput}
                onBlur={handleBlurRange}
                aria-label={`Min ${filter.name}`}
              />

              <CurrencyInput
                data-testid={DATA_TEST_IDS.INPUT('currency-max')}
                className="h-11 w-[77px] border border-gray-100 text-center font-medium text-18/26 p-2"
                value={
                  // @ts-ignore
                  filterState[filter.filterOptionId]?.max ?? filter.max
                }
                decimalsLimit={0}
                min={
                  // @ts-ignore
                  filterState[filter.filterOptionId]?.min ?? filter.min ?? 0
                }
                max={filter.max ?? MAX_PRICE}
                prefix={filter.hideCurrencySymbol ? '' : '$'}
                onValueChange={handleValueChangeRangeMaxInput}
                onBlur={handleBlurRange}
                aria-label={`Max ${filter.name}`}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ProductFilter({
  filterApplied,
  options,
  onClickApplyFilters,
}: {
  filterApplied: FilterStateType;
  options: FilterOption[];
  onClickApplyFilters: (filterState: FilterStateType) => void;
}) {
  const firstFilterOptionLabel = options[0]?.name ?? '';
  const filtersRef = useRef({filters: options, filterApplied});

  const expandedDefault = useDefaultExpandedData(filtersRef.current);
  const [expanded, setExpanded] = useState<{[key: string]: boolean}>({
    [firstFilterOptionLabel]: true,
    ...expandedDefault,
  });
  const [filterState, setFilterState] = useState(filterApplied);

  useEffect(() => {
    setFilterState(filterApplied);
  }, [filterApplied]);

  const handleClickRange = useCallback(
    (filter: FilterOption, option: (undefined | number)[]) => {
      if (filter.selectType === FILTER_SELECT_TYPES.RANGE) {
        setFilterState((prev) => {
          // Just arbitrary number if code ever reaches to default config which shouldn't in ideal world.
          const currentValue = (prev[filter.filterOptionId] as {
            min: number;
            max: number;
          }) ?? {
            min: filter.min ?? 0,
            max: filter.max ?? MAX_PRICE,
          };
          const newValue = {
            min: option[0] ?? currentValue.min,
            max: option[1] ?? currentValue.max,
          };
          return {
            ...prev,
            [filter.filterOptionId]: newValue,
          };
        });
      } else {
        console.error(
          `ProductFilter unknown selectType: ${filter.selectType} for ${filter.name}`,
        );
      }
    },
    [],
  );

  const handleClickOption = useCallback(
    (
      filter: FilterOption,
      option: {value: string} | (undefined | number)[],
    ) => {
      // Handle option being array in a separate if to ts not complain
      if (Array.isArray(option)) {
        handleClickRange(filter, option);
      } else if (filter.selectType === FILTER_SELECT_TYPES.SINGLE) {
        setFilterState((prev) => {
          const newValue =
            prev[filter.filterOptionId] === option.value
              ? undefined
              : option.value;
          return {
            ...prev,
            [filter.filterOptionId]: newValue,
          };
        });
      } else if (filter.selectType === FILTER_SELECT_TYPES.MULTIPLE) {
        setFilterState((prev) => {
          let currentValue = (prev[filter.filterOptionId] as string[]) ?? [];
          currentValue = Array.isArray(currentValue)
            ? currentValue
            : [currentValue];
          const newValue = currentValue.includes(option.value)
            ? currentValue.filter((v) => v !== option.value)
            : [...currentValue, option.value];

          // Clear the value
          if (newValue.length === 0) {
            return {
              ...prev,
              [filter.filterOptionId]: undefined,
            };
          }
          return {
            ...prev,
            [filter.filterOptionId]: newValue,
          };
        });
      } else {
        console.error(
          `ProductFilter unknown selectType: ${filter.selectType} for ${filter.name}`,
        );
      }
    },
    [handleClickRange],
  );

  const debouncedApplyFiltersRef = useRef(null);

  // Extract the serialization of filterState to a separate variable
  const serializedFilterState = JSON.stringify(filterState);

  useEffect(() => {
    // @ts-ignore
    debouncedApplyFiltersRef.current = throttle(
      300,
      () => onClickApplyFilters(filterState),
      {debounceMode: true},
    );

    return () => {
      if (debouncedApplyFiltersRef.current) {
        // @ts-ignore
        debouncedApplyFiltersRef.current.cancel();
      }
    };
  }, [serializedFilterState, onClickApplyFilters]);

  useEffect(() => {
    if (debouncedApplyFiltersRef.current) {
      // @ts-ignore
      debouncedApplyFiltersRef.current();
    }
  }, [serializedFilterState]);

  return (
    <>
      {options.map((filter, index) => (
        <ProductFilterItem
          key={filter.name}
          filter={filter}
          filterState={filterState}
          isLast={index === options.length - 1}
          expanded={expanded[filter.name]}
          setExpanded={setExpanded}
          onClickOption={handleClickOption}
        />
      ))}
    </>
  );
}
